import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { TaskStatusDef } from '../types';
import { TICKET_STATUS_LABELS_EN, TICKET_STATUS_LABELS_HE, isTicketStatus } from '../utils/ticketStatus.shared';

/**
 * B.13 — task statuses are a controlled reference list served by
 * GET /api/task-statuses. Labels come from there (he/en); callers fall back to
 * the i18n labels while it loads or for non-task statuses.
 */
/**
 * The five built-in workflow keys keep the app's own wording (milestoneStatus.*), so a
 * status reads the same in every badge, select and stepper (the server list says
 * "תכנון" / "בבדיקה" for planning / in_review, the app "בתכנון" / "בסקירה").
 * The server label is used only for statuses the app does not know.
 */
const BUILT_IN_STATUSES = new Set(['planning', 'in_progress', 'in_review', 'testing', 'completed']);
/** D-146 — keys only a service request holds. */
const TICKET_ONLY = new Set(['new', 'waiting_customer', 'closed']);

export function useTaskStatuses() {
  const { t, i18n } = useTranslation();
  const isAuthed = useAuthStore((s) => !!s.user);
  const { data } = useQuery({
    queryKey: ['task-statuses'],
    queryFn: () => api.getTaskStatuses(),
    enabled: isAuthed,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
  const statuses: TaskStatusDef[] = data?.data ?? [];
  const isHe = (i18n.language || 'he').startsWith('he');
  /**
   * D-146 — a service request ("קריאה", `taskType` 'ticket') reads its own
   * words ("בטיפול", "הושלמה"…); the ticket-only keys always do.
   */
  const labelOf = (key: string, taskType?: string | null): string | undefined => {
    if (isTicketStatus(key) && (taskType === 'ticket' || TICKET_ONLY.has(key))) {
      return (isHe ? TICKET_STATUS_LABELS_HE : TICKET_STATUS_LABELS_EN)[key];
    }
    if (BUILT_IN_STATUSES.has(key)) return t(`milestoneStatus.${key}`);
    const s = statuses.find((x) => x.key === key);
    if (!s) return undefined;
    return isHe ? s.label_he : s.label_en;
  };
  return { statuses, labelOf };
}
